<script setup lang="ts">
  import type { DocumentSendRecipientType } from '@/entities/DocumentSend';
  import { Chip, SearchBar } from '@/shared/ui';

  import type { RecipientOption } from '../model/useDocumentSendRecipientReport';

  const props = defineProps<{
    filteredRecipients: RecipientOption[];
    isLoadingRecipients: boolean;
    searchTerm: string;
    selectedRecipient: RecipientOption | null;
    selectedRecipientType: DocumentSendRecipientType | '';
  }>();

  const emit = defineEmits<{
    (e: 'update:recipientType', value: DocumentSendRecipientType): void;
    (e: 'update:searchTerm', value: string): void;
    (e: 'select', value: RecipientOption): void;
    (e: 'clear-selection'): void;
  }>();

  const shouldShowSearchResults = () =>
    props.selectedRecipientType !== '' &&
    props.searchTerm.trim() !== '' &&
    props.selectedRecipient === null;
</script>

<template>
  <section
    class="flex flex-col gap-8"
    data-testid="document-send-recipient-selector"
  >
    <div class="flex flex-col gap-4">
      <span class="text-sm font-semibold text-gray-07">Tipo de Destinatário:</span>

      <div class="flex flex-wrap gap-3">
        <label class="flex items-center gap-2 text-sm text-gray-07">
          <input
            type="radio"
            name="recipient-type"
            value="CLIENT"
            :checked="selectedRecipientType === 'CLIENT'"
            data-testid="recipient-type-client"
            @change="emit('update:recipientType', 'CLIENT')"
          >
          Cliente
        </label>

        <label class="flex items-center gap-2 text-sm text-gray-07">
          <input
            type="radio"
            name="recipient-type"
            value="SUPPLIER"
            :checked="selectedRecipientType === 'SUPPLIER'"
            data-testid="recipient-type-supplier"
            @change="emit('update:recipientType', 'SUPPLIER')"
          >
          Fornecedor
        </label>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <SearchBar
        :initial-search-term="searchTerm"
        :no-container="false"
        data-testid="recipient-search-input"
        @search="emit('update:searchTerm', $event)"
      />

      <Chip
        v-if="selectedRecipient"
        class="w-fit"
        :label="selectedRecipient.name"
        data-testid="recipient-selected-chip"
        @remove="emit('clear-selection')"
      />

      <div
        v-if="shouldShowSearchResults()"
        class="rounded-2xl border border-gray-02 bg-white-01"
        data-testid="recipient-search-results"
      >
        <div
          v-if="isLoadingRecipients"
          class="px-4 py-4 text-sm text-gray-03"
        >
          Carregando destinatários...
        </div>
        <div
          v-else-if="filteredRecipients.length === 0"
          class="px-4 py-4 text-sm text-gray-03"
        >
          Nenhum destinatário encontrado.
        </div>
        <div
          v-else
          class="max-h-72 overflow-y-auto"
        >
          <button
            v-for="recipient in filteredRecipients"
            :key="recipient.id"
            type="button"
            class="flex w-full flex-col items-start gap-1 border-b border-gray-02 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-01"
            :class="selectedRecipient?.id === recipient.id ? 'bg-gray-01' : 'bg-white-01'"
            :data-testid="`recipient-option-${recipient.id}`"
            @click="emit('select', recipient)"
          >
            <span class="text-sm font-medium text-gray-07">{{ recipient.name }}</span>
            <span class="text-xs text-gray-03">{{
              recipient.email ?? 'Sem e-mail cadastrado'
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
